import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@core/api/api.service';
import { EnvConfig } from '@core/config';
import { Observable, BehaviorSubject } from 'rxjs';

export type TemplateNameDdlPayload = {
  partner_uid: string | null;
  emittent_company_uid: string | null;
  template_type: string;
  is_standard: 'Y' | 'N';
  created_by_uid: string | null;
  created_for_user_id?: string | null;
  is_client: 'Y' | 'N';
};

export type TemplateWiseCountPayload = {
  partner_uid: string | null;
  emittent_company_uid: string | null;
  category: string;
  created_by_uid: string | null;
  created_for_user_id?: string | null;
  is_client: 'Y' | 'N';
};

export type DeleteTemplatePayload = {
  templatedetails_id: number;
  user_uid: string;
};

export type UpdateTemplateDetailsStatusPayload = {
  templatedetails_id: number;
  status: string;
  user_uid: string | null;
};

export type UpdateTemplateFormattedQuestionPayload = {
  templatequestion_id: number | string;
  formatted_question: string;
  modified_by_uid: string;
};

export type GetTemplateFormattedQuestionAuditPayload = {
  templatequestion_id: number | string;
};

export interface TemplateFormattedQuestionAuditRecord {
  audit_id: number;
  version_no: string | number;
  operation_type: string | null;
  new_formatted_question: string | null;
  old_formatted_question: string | null;
  modified_by: string | null;
  modified_date: string | null;
}

export type TemplateTypeWiseTemplateDetailsPayload = {
  template_type: string;
  created_by_uid: string | null;
  // created_for_user_id?: string | null;
  // is_analyst: string;
  partner_uid?: string | null;
  emittent_company_uid?: string | null;
  // is_superuser?: string;
  is_client: 'Y' | 'N';
  category: string;
};

export type GetTemplatesByAssetDdlPayload = {
  partner_uid: string | null;
  scope_type: 'PARTNER' | 'EMITTENT' | 'CLIENT';
  scope_uid: string;
  is_standard: 'Y' | 'N';
  template_type: 'DD' | 'IM';
  asset_class_ids: number | number[] | null;
  asset_subclass_ids: number[] | null;
};

export type UpdateDefaultTemplatePayload = {
  id: number | null;
  source_scope_type: 'PARTNER' | 'EMITTENT' | 'CLIENT' | null;
  partner_uid: string | null;
  scope_type: 'PARTNER' | 'EMITTENT' | 'CLIENT';
  scope_uid: string;
  template_id: number;
  asset_class_ids: number | number[] | null;
  asset_subclass_ids: number[] | null;
  mapping_ids: number[];
  modified_by_uid: string;
};

@Injectable({
  providedIn: 'root',
})
export class TemplateDDService {
  // ================================
  // 📌 GET BEHAVIOR SUBJECT
  // ================================
  private behaviorSubject = new BehaviorSubject<any>(null);
  public behaviorSubject$ = this.behaviorSubject.asObservable();

  private ddBehaviorSubject = new BehaviorSubject<boolean>(false);
  public ddBehaviorSubject$ = this.ddBehaviorSubject.asObservable();

  private isEditBehaviorSubject = new BehaviorSubject<string>('Y');
  public isEditBehaviorSubject$ = this.isEditBehaviorSubject.asObservable();

  private selectedDD = new BehaviorSubject<string>(null);
  public selectedDD$ = this.selectedDD.asObservable();

  constructor(private apiService: ApiService, private env: EnvConfig) {}

  private get baseUrl(): string {
    return this.env.aiEngineApiUrl || '';
  }

  private getHeaders() {
    return {
      headers: new HttpHeaders({
        accept: 'application/json',
        'Content-Type': 'application/json',
      }),
    };
  }

  private normalizeNumericArray(value: unknown): number[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item));
  }

  private normalizeNullableNumericArray(value: unknown): number[] | null {
    if (value == null) {
      return null;
    }

    const normalizedValues = this.normalizeNumericArray(value);
    return normalizedValues.length > 0 ? normalizedValues : null;
  }

  private normalizeNullableAssetClassIds(value: unknown): number[] | null {
    if (value == null) {
      return null;
    }

    return this.normalizeNullableNumericArray(value);
  }

  // ================================
  // 📌 SET BEHAVIOR SUBJECT VALUE
  // ================================
  public setBehaviorSubjectValue(value: any): void {

    this.behaviorSubject.next(value);
  }

  // ================================
  // 📌 GET BEHAVIOR SUBJECT VALUE
  // ================================
  public getBehaviorSubjectValue(): any {
    return this.behaviorSubject.getValue();
  }

  // ================================
  // 📌 CREATE TEMPLATE
  // ================================
  public createTemplate(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/sp_create_template_from_json', payload, this.getHeaders());
  }

  // ================================
  // 📌 CLONE TEMPLATE (COPY OLD TEMPLATE DATA)
  // ================================
  public copyOldTemplateDataFromJson(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/sp_copy_old_template_data_from_json', payload, this.getHeaders());
  }

  /**
   * Updates template metadata (name, partner, assets / sub-assets). Uses the same endpoint as
   * {@link createTemplate} with a non-null `templatedetails_id` in the payload.
   */
  public updateTemplate(payload: any): Observable<any> {
    return this.createTemplate(payload);
  }
  
  public deleteTemplate(payload: DeleteTemplatePayload): Observable<any> {
    return this.apiService.post(this.baseUrl + '/sp_delete_template', payload, this.getHeaders());
  }

  public updateTemplateDetailsStatus(payload: UpdateTemplateDetailsStatusPayload): Observable<any> {
    return this.apiService.post(this.baseUrl + '/update_templatedetails_status', payload, this.getHeaders());
  }

  // ================================
  // 📌 GET TEMPLATE DETAILS BY ID
  // ================================
  public getTemplateDetailsById(payload: any): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/sp_get_template_questions_with_details_json',
      payload,
      this.getHeaders(),
    );
  }

  // // ================================
  // // 📌 UPDATE QUESTION TEMPLATE
  // // ================================
  public updateTemplateQuestion(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/sp_update_template_question_from_json', payload, this.getHeaders());
  }

  // ================================
  // 📌 UPDATE QUESTION TEMPLATE
  // ================================
  public updateQuestion(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/add-edit-que', payload);
  }

  public updateTemplateFormattedQuestion(payload: UpdateTemplateFormattedQuestionPayload): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/sp_update_template_formatted_question',
      payload,
      this.getHeaders(),
    );
  }

  public getTemplateFormattedQuestionAudit(
    payload: GetTemplateFormattedQuestionAuditPayload,
  ): Observable<TemplateFormattedQuestionAuditRecord[]> {
    return this.apiService.post<TemplateFormattedQuestionAuditRecord[]>(
      this.baseUrl + '/get_template_formattedquestion_audit',
      payload,
      this.getHeaders(),
    );
  }

  // ================================
  // 📌 SELECT / SAVE RECOMMENDATION
  // ================================
  public selectRecommendation(payload: any): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/sp_update_template_question_with_unmatched_json',
      payload,
      this.getHeaders(),
    );
  }

  public insertTemplateQuestionWithUnmatchedJsonIntoLibrary(payload: any): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/insert_template_question_with_unmatched_json_into_library',
      payload,
      this.getHeaders(),
    );
  }

  public upsertQuestionToVectorStore(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/vector-stores/upsert-que', payload, this.getHeaders());
  }

  // ================================
  // 📌 DELETE QUESTION
  // Payload example:
  // {
  //   "templatequestion_id": 301,
  //   "deleted_by_uid": "11111111-2222-3333-4444-555555555555"
  // }
  // ================================
  public deleteQuestion(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/sp_soft_delete_template_question_json', payload, this.getHeaders());
  }

  // ================================
  // 📌 DELETE SECTION OR SUBSECTION
  // ================================
  public deleteSectionOrSubsection(payload: any): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/sp_soft_delete_section_or_subsection_json',
      payload,
      this.getHeaders(),
    );
  }

  // ================================
  // UPDATE SECTION OR SUBSECTION
  // ================================
  public updateSectionOrSubsection(payload: any): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/sp_update_section_or_subsection_json',
      payload,
      this.getHeaders(),
    );
  }

  getTemplateTypeWiseTemplateDetails(payload: TemplateTypeWiseTemplateDetailsPayload): Observable<any> {
    return this.apiService.post(this.baseUrl + '/get_template_type_wise_template_details', payload, this.getHeaders());
  }

  getTemplateNameDdl(payload: TemplateNameDdlPayload): Observable<any> {
    return this.apiService.post(this.baseUrl + '/get_template_name_ddl', payload, this.getHeaders());
  }

  getTemplatesByAssetDdl(payload: GetTemplatesByAssetDdlPayload): Observable<any> {
    const normalizedPayload: GetTemplatesByAssetDdlPayload = {
      ...payload,
      asset_class_ids: this.normalizeNullableAssetClassIds(payload?.asset_class_ids),
      asset_subclass_ids: this.normalizeNullableNumericArray(payload?.asset_subclass_ids),
    };

    return this.apiService.post(this.baseUrl + '/get_templates_by_asset_ddl', normalizedPayload, this.getHeaders());
  }

  getAssetDetails(payload: { product_uid: string }): Observable<any> {
    return this.apiService.post(this.baseUrl + '/pr_get_asset_details', payload, this.getHeaders());
  }

  updateDefaultTemplate(payload: UpdateDefaultTemplatePayload): Observable<any> {
    const normalizedPayload: UpdateDefaultTemplatePayload = {
      ...payload,
      id: payload?.id == null ? null : Number(payload.id),
      source_scope_type: payload?.source_scope_type ?? null,
      asset_class_ids: this.normalizeNullableAssetClassIds(payload?.asset_class_ids),
      asset_subclass_ids: this.normalizeNullableNumericArray(payload?.asset_subclass_ids),
      mapping_ids: this.normalizeNumericArray(payload?.mapping_ids),
    };

    return this.apiService.post(this.baseUrl + '/update_default_template_calc_score', normalizedPayload, this.getHeaders());
  }

  /**
   * Returns dashboard-style counts (DD/IM templates etc) grouped by partner/company context.
   * Backend SP: `get_template_wise_count_by_partner_company_id`.
   */
  getTemplateWiseCountByPartnerCompanyId(payload: TemplateWiseCountPayload): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/get_template_wise_count_by_partner_company_id',
      payload,
      this.getHeaders(),
    );
  }

  getTemplateQuestionHistory(payload: any): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/get_templatequestionhistory_by_templatequestion_id',
      payload,
      this.getHeaders(),
    );
  }

  // ================================
  // 📌 GET ASSET CLASSES
  // ================================
  public getAssetClasses(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/get_dropdown_list_data', payload, this.getHeaders());
  }

  /** Same endpoint as {@link getAssetClasses}; use `listName` (e.g. `'Organisation'`) in the payload. */
  public getOrganisations(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/get_dropdown_list_data', payload, this.getHeaders());
  }

  /** Same endpoint as {@link getOrganisations}; use `listName: 'Partner'` in the payload. */
  public getPartners(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/get_dropdown_list_data', payload, this.getHeaders());
  }

  // ================================
  // 📌 GET SUB ASSET CLASSES
  // ================================
  public getSubAssetClasses(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/get_dropdown_list_data', payload, this.getHeaders());
  }

  // ================================
  // 📌 GET TEMPLATE DETAILS BY ID
  // ================================
  public runQueModule(payload: any): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/run-que-module',
      payload
    );
  }

  // =======================================
  // 📌 UPDATE QUESTION TEMPLATE IS PRIVATE
  // =======================================
  public updateTemplatequestionIsPrivate(payload: any): Observable<any> {
    return this.apiService.post(this.baseUrl + '/update_templatequestion_is_private', payload);
  }

  // ================================
  // GET MISSING QUESTIONS
  // ================================
  public getMissingQuestions(payload: {
    templatedetails_id: number | string | null;
  }): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/get_template_templatemissingquestion_in_json',
      payload,
      this.getHeaders(),
    );
  }

  // ================================
  // GET TEMPLATE FILE
  // ================================
  public getTemplateFile(payload: {
    templatedetails_id: number | string | null;
    partner_uid: string | null;
  }): Observable<Blob> {
    return this.apiService.post(this.baseUrl + '/get-template-file', payload, {
      ...this.getHeaders(),
      responseType: 'blob' as 'json',
    });
  }

  // ================================
  // BULK INSERT MISSING QUESTIONS
  // ================================
  public bulkInsertMissingQuestions(payload: {
    questions: any[];
    vs_id: string;
    created_by_uid: string;
    templatedetails_id: string;
  }): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/bulk-insert-missing-questions',
      payload,
      this.getHeaders(),
    );
  }

  // ================================
  // BULK UPDATE QUESTION ORDER
  // ================================
  public bulkUpdateQuestionOrder(payload: {
    templatedetails_id: number;
    questions: Array<{
      templatequestion_id: number;
      order: number;
    }>;
  }): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/bulk_update_question_order_json',
      payload,
      this.getHeaders(),
    );
  }

  // ================================
  // BULK UPDATE SECTION/SUBSECTION ORDER
  // ================================
  public bulkUpdateSectionSubsectionOrder(payload: {
    templatedetails_id: number;
    level: 'SECTION' | 'SUB_SECTION';
    section?: string;
    items: Array<{
      section?: string;
      sub_section?: string;
      order: number;
    }>;
  }): Observable<any> {
    return this.apiService.post(
      this.baseUrl + '/bulk_update_section_subsection_order',
      payload,
      this.getHeaders(),
    );
  }
}
